import { ReactElement } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import lazyRoute from '../util/lazyRoute';
import { fleetToConsumerPath, consumerToFleetPath } from './paths';
import { useSkin } from './skin';
import { useStepProgress } from './SkinSwitchLink';

/*
 * The consumer skin's screens are one chunk of their own ("consumer"): a
 * Go Oil visitor, or an Aperio visitor on today's flow, never downloads
 * them. Every page here is a booking step behind the same Redux state.
 */
export const ConsumerShell = lazyRoute(() => import(
  /* webpackChunkName: "consumer" */ './ConsumerShell'
));
export const ConsumerAddress = lazyRoute(() => import(
  /* webpackChunkName: "consumer" */ './steps/AddressStep'
));
export const ConsumerCar = lazyRoute(() => import(
  /* webpackChunkName: "consumer" */ './steps/CarStep'
));
export const ConsumerPlan = lazyRoute(() => import(
  /* webpackChunkName: "consumer" */ './steps/PlanStep'
));
export const ConsumerDay = lazyRoute(() => import(
  /* webpackChunkName: "consumer" */ './steps/DayStep'
));
export const ConsumerDetails = lazyRoute(() => import(
  /* webpackChunkName: "consumer" */ './steps/DetailsStep'
));
export const ConsumerConfirmation = lazyRoute(() => import(
  /* webpackChunkName: "consumer" */ './ConsumerConfirmation'
));

/** One of two elements, by the skin in effect (always `fleet` off Aperio). */
export function BySkin({
  fleet,
  consumer,
}: {
  fleet: ReactElement;
  consumer: ReactElement;
}) {
  return useSkin() === 'consumer' ? consumer : fleet;
}

/**
 * A page of today's flow. On the consumer skin it sends the visitor to the
 * consumer step that asks the same question (Back, an old link, a switch).
 */
export function FleetOnly({ children }: { children: ReactElement }) {
  const skin = useSkin();
  const { pathname } = useLocation();
  const progress = useStepProgress();
  if (skin !== 'consumer') return children;
  return <Navigate replace to={fleetToConsumerPath(pathname, progress)} />;
}

/** A consumer step. On today's flow it goes to the matching page. */
export function ConsumerOnly({ children }: { children: ReactElement }) {
  const skin = useSkin();
  const { pathname } = useLocation();
  const progress = useStepProgress();
  if (skin === 'consumer') return children;
  return <Navigate replace to={consumerToFleetPath(pathname, progress)} />;
}
